import { Feature, Map } from "ol";
import SimpleGeometry from "ol/geom/SimpleGeometry";
import { Draw } from "ol/interaction";
import VectorLayer from "ol/layer/Vector";
import { Icon, Style } from "ol/style";
import React from "react";

export type PolygonCoords = number[][][];

type UseDrawProps<F extends SimpleGeometry> = {
  map: Map | undefined;
  enabled?: boolean;
  drawLayer: VectorLayer;
  GeometryClass: new (args: ReturnType<F["getCoordinates"]>) => F;
  coordinates?: ReturnType<F["getCoordinates"]>;
  drawIcon?: string;
};

// type Return = {
//   drawLayer: Layer<Source> | undefined;
//   setItemCoordinates: React.Dispatch<React.SetStateAction<number[]>>;
//   itemCoordinates: number[];
// };

export function getPoiSvgStyle(icon: string | undefined) {
  return new Style({
    image: new Icon({
      src: `${window.location.origin}${icon}`,
      scale: 1.5,
    }),
  });
}
// TODO NOT IN USE
export const useGenericDraw = <T extends SimpleGeometry>({
  map,
  coordinates = [] as ReturnType<T["getCoordinates"]>,
  enabled = true,
  GeometryClass,
  drawLayer,
  drawIcon,
}: UseDrawProps<T>) => {
  const [itemCoordinates, setItemCoordinates] = React.useState(coordinates);

  // allows to change point placement via input
  React.useEffect(() => {
    const source = drawLayer?.getSource();
    if (!source) return;
    const poiFeature = source.getFeatures()[0];
    if (!poiFeature) {
      source.addFeature(
        new Feature({ geometry: new GeometryClass(itemCoordinates) }),
      );
    } else {
      const geometry = new GeometryClass(itemCoordinates);
      poiFeature.setGeometry(geometry);
    }
  }, [GeometryClass, drawLayer, itemCoordinates]);

  React.useEffect(() => {
    const source = drawLayer?.getSource();
    if (!map || !source || !enabled) return;

    const draw = new Draw({
      source,
      type: new GeometryClass(itemCoordinates).getType(),
      style: drawIcon ? getPoiSvgStyle(drawIcon) : undefined,
    });

    map?.addInteraction(draw);

    draw.on("drawstart", () => {
      source.clear();
    });

    draw.on("drawend", (e) => {
      const c = e.feature.getGeometry() as T;

      if (!c) return;
      const coords = c.getCoordinates() as typeof coordinates;
      setItemCoordinates(coords);
    });
    // eslint-disable-next-line consistent-return
    return () => {
      map.removeInteraction(draw);
    };
  }, [
    drawLayer,
    enabled,
    map,
    itemCoordinates.length,
    GeometryClass,
    itemCoordinates,
    drawIcon,
  ]);

  const clearDrawing = React.useCallback(() => {
    const source = drawLayer?.getSource();
    if (!source) return;
    setItemCoordinates([] as typeof coordinates);
    source.clear();
  }, [drawLayer]);

  return {
    drawLayer,
    clearDrawing,
    setItemCoordinates,
    itemCoordinates,
  };
};
